/**
 * ConfirmEmailNudge (T48) — the non-blocking in-app "Confirm your email" banner.
 *
 * T48 took email confirmation off the signup critical path: a new user is logged
 * in immediately with `emailVerified === false`, and this floating banner is the
 * gentle, dismissible nudge to confirm — surfaced AFTER the user has used the app
 * a bit, never on the first screen.
 *
 * Gates (all must hold to render):
 *   - the signed-in user's `emailVerified` is false (hidden permanently once
 *     verified — the user's `/me` flips it true after they click the link);
 *   - this is the SECOND session onward (a per-device session counter; the nudge
 *     never shows on the very first session, so a brand-new user reaches the app
 *     unbothered);
 *   - it hasn't been dismissed this session (a module flag; re-shows next load).
 *
 * "Resend" re-sends the confirmation link (→ /resend-verification); "Dismiss"
 * hides it for the session. Mounted once in the authenticated AppShell. Pinned
 * above the bottom nav, mirroring DonationBanner. Token-only (CP5); mobile-first;
 * ≥44px targets.
 */

import { useState, type CSSProperties } from 'react';
import { useMe, useResendVerification } from '@shopista/shared/api/hooks';

/**
 * Per-device session counter. Incremented once per app load by `bumpSessionCount`
 * (called from the app entry). The nudge shows only from the SECOND session on,
 * so a brand-new user's first session is nudge-free.
 */
const SESSION_COUNT_KEY = 'shopista.sessionCount';

/** Read the current session count (0 when unset / unreadable). */
export function readSessionCount(): number {
  try {
    const raw = window.localStorage.getItem(SESSION_COUNT_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Increment the per-device session count by one. Call ONCE per app load (app
 * entry / main.tsx) so "session" means "distinct app open", not "render".
 */
export function bumpSessionCount(): void {
  try {
    window.localStorage.setItem(SESSION_COUNT_KEY, String(readSessionCount() + 1));
  } catch {
    // Quota / disabled — the nudge simply stays gated off; harmless.
  }
}

// Session-scoped dismissal: a plain module flag, reset on a full reload (parity
// with DonationBanner). Deliberately NOT persisted — the nudge re-shows next load
// until the user confirms.
let dismissedThisSession = false;

// Exported for ConfirmEmailNudge.test.tsx — the test asserts on this source
// object rather than reading via CSSOM (happy-dom drops nested calc/var).
//
// BUG51 — this used to also carry `position: 'fixed'` + `left`/`right`/
// `bottom`/`zIndex`, identical to LocationNeededBanner's own `bannerStyle`.
// When both were eligible at once (fresh signup: unverified + no location)
// they occupied the exact same rect — whichever painted second (this one, or
// the location banner) sat on top and absorbed the other's clicks. AppShell's
// shared `ambientBannerStackStyle` now owns the fixed positioning + z-index
// for both; this object is just the row's own visual + layout styling, so it
// renders as a plain flex child that stacks instead of floating independently.
export const nudgeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  borderRadius: 'var(--radius-lg)',
  backgroundColor: 'var(--color-accent-subtle)',
  boxShadow: 'var(--shadow-lg)',
};

const artStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-bg-raised)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-base)',
};

const mainStyle: CSSProperties = { flex: 1, minWidth: 0 };

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  lineHeight: 1.3,
};

const descStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
};

const ctaStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const closeStyle: CSSProperties = {
  flex: '0 0 auto',
  alignSelf: 'flex-start',
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  margin: 'calc(var(--space-2) * -1) calc(var(--space-2) * -1) calc(var(--space-2) * -1) 0',
  border: 0,
  background: 'transparent',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1,
  cursor: 'pointer',
  borderRadius: 'var(--radius-pill)',
  display: 'grid',
  placeItems: 'center',
};

export function ConfirmEmailNudge(): JSX.Element | null {
  const me = useMe();
  const resend = useResendVerification();
  const [dismissed, setDismissed] = useState(dismissedThisSession);
  // True once Resend succeeds — the copy flips to a sent confirmation in place,
  // and the nudge stays (the user can dismiss it) rather than vanishing silently.
  const [sent, setSent] = useState(false);

  function dismiss(): void {
    dismissedThisSession = true;
    setDismissed(true);
  }

  // Gate: only a confirmed-as-unverified, second-session-onward, non-dismissed
  // signed-in user sees it. `emailVerified` absent (older payload) is treated as
  // unverified, but the session-count gate still keeps it off the first session.
  if (dismissed) return null;
  if (me.status !== 'success' || !me.data) return null;
  if (me.data.emailVerified === true) return null;
  if (readSessionCount() < 2) return null;

  return (
    <div
      style={nudgeStyle}
      role="complementary"
      aria-label="Confirm your email"
      data-testid="confirm-email-nudge"
    >
      <div style={artStyle} aria-hidden="true">
        ✉️
      </div>
      <div style={mainStyle}>
        {sent ? (
          <>
            <div style={titleStyle}>Confirmation link sent</div>
            <div style={descStyle}>Check your inbox (and spam) for the link.</div>
          </>
        ) : (
          <>
            <div style={titleStyle}>Confirm your email</div>
            <div style={descStyle}>Tap the link we emailed you to secure your account.</div>
          </>
        )}
      </div>
      {!sent && (
        <button
          type="button"
          style={ctaStyle}
          disabled={resend.isPending}
          onClick={() =>
            resend.mutate(undefined, {
              onSuccess: () => setSent(true),
            })
          }
          data-testid="confirm-email-resend"
        >
          {resend.isPending ? 'Sending…' : 'Resend'}
        </button>
      )}
      <button
        type="button"
        style={closeStyle}
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="confirm-email-dismiss"
      >
        ✕
      </button>
    </div>
  );
}
